/**
 * Location utility functions
 */

import { getName, getCode } from 'country-list';

/**
 * Get the current user's geolocation
 * @returns {Promise<Object>} Promise resolving to coordinates object
 */
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        let errorMessage = 'Unknown error occurred while getting location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        
        reject(new Error(errorMessage));
      },
      { 
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000 
      }
    );
  });
};

/**
 * Get location name (city, town) from coordinates using Mapbox reverse geocoding
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<Object>} Location data including place names
 */
export const getLocationNameFromCoordinates = async (latitude, longitude) => {
  // Get Mapbox token from environment variables
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  
  if (!token) {
    console.warn('Mapbox token not found. Using fallback location name.');
    return {
      displayName: 'Current location',
      city: null,
      state: null,
      country: null,
      countryCode: null,
      coordinates: { latitude, longitude }
    };
  }
  
  try {
    // Construct Mapbox reverse geocoding URL - add country to types
    // Types filter for place, locality, neighborhood and country prioritizes city/town names and includes country data
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${token}&types=place,locality,neighborhood,country`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Geocoding request failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Default values
    let displayName = 'Current location';
    let city = null;
    let state = null;
    let country = null;
    let countryCode = null;
    
    if (data.features && data.features.length > 0) {
      // Find the most appropriate place name (city or locality)
      const placeFeature = data.features.find(f => 
        f.place_type.includes('place') || 
        f.place_type.includes('locality') ||
        f.place_type.includes('neighborhood')
      );
      
      // Find country data
      const countryFeature = data.features.find(f => 
        f.place_type.includes('country')
      );
      
      if (placeFeature) {
        displayName = placeFeature.text;
        city = placeFeature.text;
        
        // Extract state/region and country from context
        if (placeFeature.context) {
          // Find state/region
          const stateData = placeFeature.context.find(c => c.id.includes('region'));
          if (stateData) {
            state = stateData.text;
          }
          
          // Find country data
          const countryData = placeFeature.context.find(c => c.id.includes('country'));
          if (countryData) {
            country = countryData.text;
            // Extract country code from short_code (format: "country.code")
            countryCode = countryData.short_code ? 
              countryData.short_code.split('.')[1]?.toUpperCase() : null;
          }
        }
      }
      
      // If country wasn't found in context, use the country feature directly
      if (!country && countryFeature) {
        country = countryFeature.text;
        countryCode = countryFeature.properties?.short_code ? 
          countryFeature.properties.short_code.toUpperCase() : null;
      }
    }
    
    
    // Return structured location data
    return {
      displayName,
      city,
      state,
      country,
      countryCode,
      coordinates: { latitude, longitude }
    };
  } catch (error) {
    console.error('Error getting location name:', error);
    // Return fallback with coordinates only
    return {
      displayName: 'Current location',
      city: null,
      state: null,
      country: null,
      countryCode: null,
      coordinates: { latitude, longitude }
    };
  }
};

/**
 * Calculate distance between two geographical points using Haversine formula
 * @param {Object} point1 - First point with latitude and longitude
 * @param {Object} point2 - Second point with latitude and longitude
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (point1, point2) => {
  if (!point1 || !point2 || !point1.latitude || !point1.longitude || 
      !point2.latitude || !point2.longitude) {
    return null;
  }
  
  // Earth's radius in kilometers
  const earthRadius = 6371;
  
  const lat1 = parseFloat(point1.latitude);
  const lon1 = parseFloat(point1.longitude);
  const lat2 = parseFloat(point2.latitude);
  const lon2 = parseFloat(point2.longitude);
  
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return earthRadius * c;
};

/**
 * Convert kilometers to meters
 * @param {number} km - Distance in kilometers
 * @returns {number} Distance in meters
 */
export const kmToMeters = (km) => {
  if (typeof km !== 'number') return 0;
  return km * 1000;
};

/**
 * Convert degrees to radians
 * @param {number} deg - Angle in degrees
 * @returns {number} Angle in radians
 */
export const deg2rad = (deg) => {
  return deg * (Math.PI / 180);
};

/**
 * Get Google Maps URL for an address
 * @param {Object} address - Address object
 * @returns {string} Google Maps URL
 */
export const getAddressMapUrl = (address) => {
  if (!address) return '';
  
  // If we have coordinates, use them for more accurate map
  if (address.latitude && address.longitude) {
    return `https://www.google.com/maps?q=${address.latitude},${address.longitude}`;
  }
  
  // Otherwise, use the formatted address
  const addressString = [
    address.street,
    address.city,
    address.state,
    address.postalCode,
    address.country
  ].filter(Boolean).join(', ');
  
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`;
};

/**
 * Get a static map URL for an address (Mapbox)
 * @param {Object} address - Address object
 * @param {number} width - Map width in pixels
 * @param {number} height - Map height in pixels
 * @param {string} accessToken - Mapbox access token
 * @returns {string} Static map URL
 */
export const getStaticMapUrl = (address, width = 600, height = 300, accessToken) => {
  if (!address || !address.latitude || !address.longitude || !accessToken) return '';
  
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s+555555(${address.longitude},${address.latitude})/${address.longitude},${address.latitude},14/${width}x${height}?access_token=${accessToken}`;
};

/**
 * Normalize country data to consistent format (always as country code)
 * Helps with comparing countries regardless of format (code vs name)
 * 
 * @param {string} country - Country as either name or code
 * @returns {string|null} - Normalized country code (uppercase) or null if invalid
 */
export const normalizeCountry = (country) => {
  if (!country) return null;
  
  // Trim and uppercase for consistent comparison
  const trimmed = country.trim();
  
  // If it's already a 2-letter code
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  
  // Try to get code from country name
  const code = getCode(trimmed);
  if (code) {
    return code.toUpperCase();
  }
  
  // Special cases for common variations
  const specialCases = {
    'USA': 'US',
    'United States': 'US',
    'UK': 'GB',
    'England': 'GB',
    'Republic of Korea': 'KR'
  };
  
  if (specialCases[trimmed]) {
    return specialCases[trimmed];
  }
  
  // Log unfound countries for debugging
  console.warn(`Could not normalize country: "${country}"`);
  return null;
};

/**
 * Compare two countries to determine if they are the same country
 * regardless of format (code vs name)
 * 
 * @param {string} country1 - First country (as name or code)
 * @param {string} country2 - Second country (as name or code)
 * @returns {boolean} - True if same country, false otherwise
 */
export const isSameCountry = (country1, country2) => {
  if (!country1 || !country2) return false;
  
  const code1 = normalizeCountry(country1);
  const code2 = normalizeCountry(country2);
  
  if (!code1 || !code2) {
    // If either couldn't be normalized, log and compare as strings
    console.warn(`Country comparison fallback to string: "${country1}" vs "${country2}"`);
    return country1.trim().toLowerCase() === country2.trim().toLowerCase();
  }
  
  return code1 === code2;
};

/**
 * Get country code from country name or code
 * 
 * @param {string} country - Country name or code
 * @returns {string|null} - Country code or null if invalid
 */
export const getCountryCode = (country) => {
  return normalizeCountry(country);
};

/**
 * Get country name from country code or name
 * 
 * @param {string} country - Country code or name
 * @returns {string|null} - Country name or null if invalid
 */
export const getCountryName = (country) => {
  const code = normalizeCountry(country);
  if (!code) return null;
  
  return getName(code) || null;
}; 